import { useEffect, useState, useMemo, useCallback } from "react";
import api from "../../lib/api";
import { toast, Toaster } from "sonner";
import { ChevronLeft, ChevronRight, Check, Banknote, Smartphone, Building2, Loader2, RotateCcw, Mail, Send } from "lucide-react";

const monthName = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("de-CH", { month: "long", year: "numeric" });
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const currentYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const MonthlyBilling = () => {
  const [ym, setYm] = useState(currentYM());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/billing/${ym}`);
      setRows(r.data.rows || []);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  const pay = async (row, paymentType) => {
    try {
      await api.post(`/admin/billing/${ym}/pay`, {
        student_id: row.student_id,
        course_id: row.course_id,
        amount: row.due_amount,
        payment_type: paymentType,
      });
      toast.success(`${row.student_name} bezahlt (${paymentType.toUpperCase()})`);
      load();
    } catch { toast.error("Fehler beim Buchen"); }
  };

  const doPay = (row, type) => {
    setBusyKey(`${row.student_id}-${row.course_id}`);
    pay(row, type).finally(() => setBusyKey(null));
  };

  const undo = async (row) => {
    if (!window.confirm(`Zahlung von ${row.student_name} rückgängig machen?`)) return;
    setBusyKey(`${row.student_id}-${row.course_id}`);
    try {
      await api.delete(`/admin/billing/${ym}/pay`, { params: { student_id: row.student_id, course_id: row.course_id } });
      toast.success("Zahlung zurückgesetzt");
      load();
    } catch { toast.error("Fehler"); }
    finally { setBusyKey(null); }
  };

  const remind = async (row) => {
    setBusyKey(`${row.student_id}-${row.course_id}`);
    try {
      await api.post(`/admin/billing/${ym}/remind`, { student_id: row.student_id, course_id: row.course_id });
      toast.success(`Erinnerung an ${row.student_name} gesendet`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Fehler beim Senden";
      toast.error(msg);
    } finally { setBusyKey(null); }
  };

  const remindAll = async () => {
    const unpaid = rows.filter(r => !r.paid).length;
    if (unpaid === 0) { toast.info("Keine offenen Zahlungen"); return; }
    if (!window.confirm(`${unpaid} Erinnerungs-Mails versenden?`)) return;
    setBusyKey("all");
    try {
      const r = await api.post(`/admin/billing/${ym}/remind-all`);
      const { sent, skipped_no_email, failed } = r.data;
      toast.success(`${sent} Mails gesendet${skipped_no_email ? ` · ${skipped_no_email} ohne E-Mail` : ""}${failed ? ` · ${failed} fehlgeschlagen` : ""}`);
      load();
    } catch { toast.error("Fehler beim Massenversand"); }
    finally { setBusyKey(null); }
  };

  const totals = useMemo(() => {
    const due = rows.reduce((a, r) => a + (r.due_amount || 0), 0);
    const paid = rows.filter(r => r.paid).reduce((a, r) => a + (r.paid_amount || r.due_amount || 0), 0);
    const open = due - paid;
    return { due, paid, open, paidCount: rows.filter(r => r.paid).length, total: rows.length };
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="admin-monthly-billing">
      <Toaster position="top-center" theme="light" />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-3xl">Monatsbeiträge</h1>
          <p className="text-sm text-muted-foreground">Eltern zahlen Ende Monat via Twint oder bar. Hier einzeln bestätigen.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={remindAll} disabled={busyKey === "all"} data-testid="billing-remind-all"
            className="px-4 py-2 text-xs border border-primary/40 text-primary rounded-full hover:bg-primary/10 inline-flex items-center gap-2 disabled:opacity-50">
            {busyKey === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Alle Offenen erinnern
          </button>
          <button onClick={() => setYm(shiftMonth(ym, -1))} className="p-2 border border-border rounded-md hover:border-muted-foreground" data-testid="billing-prev-month"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-4 py-2 bg-secondary/40 border border-border rounded-md min-w-[180px] text-center font-medium capitalize" data-testid="billing-current-month">{monthName(ym)}</div>
          <button onClick={() => setYm(shiftMonth(ym, +1))} className="p-2 border border-border rounded-md hover:border-muted-foreground" data-testid="billing-next-month"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setYm(currentYM())} className="ml-2 text-xs px-3 py-2 border border-border rounded-md hover:border-muted-foreground">Heute</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Schüler · Kurse" value={totals.total} />
        <StatCard label="Soll-Beträge" value={`CHF ${totals.due.toFixed(2)}`} />
        <StatCard label="Bezahlt" value={`CHF ${totals.paid.toFixed(2)}`} accent="primary" />
        <StatCard label="Offen" value={`CHF ${totals.open.toFixed(2)}`} accent="destructive" />
      </div>

      <div className="card-academy rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 border-b border-border">
              <tr>
                {["Schüler", "Kurs", "Lektionen", "Preis/L.", "Betrag", "Status", "Aktion"].map(h =>
                  <th key={h} className="text-left text-xs uppercase tracking-[0.15em] text-muted-foreground px-4 py-3">{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /> Lädt…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Keine aktiven Schüler · Kurs-Kombinationen für diesen Monat.</td></tr>
              )}
              {!loading && rows.map(row => {
                const key = `${row.student_id}-${row.course_id}`;
                const busy = busyKey === key;
                return (
                  <tr key={key} className={`border-b border-border/50 ${row.paid ? "bg-primary/5" : "hover:bg-secondary/20"}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.student_name}</div>
                      <div className="text-xs text-muted-foreground">{row.parent_name || ""} {row.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{row.course_name}</td>
                    <td className="px-4 py-3 text-center">{row.lesson_count}</td>
                    <td className="px-4 py-3">CHF {row.per_lesson.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">CHF {row.due_amount.toFixed(2)}</div>
                      {row.has_flat_override && <div className="text-[10px] text-amber-600">flat-override</div>}
                    </td>
                    <td className="px-4 py-3">
                      {row.paid ? (
                        <div className="text-xs">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/15 text-primary border border-primary/30">
                            <Check className="w-3 h-3" /> bezahlt
                          </span>
                          <div className="text-muted-foreground mt-1">{row.payment_type?.toUpperCase()} · {row.paid_at}</div>
                        </div>
                      ) : (
                        <div className="text-xs">
                          <span className="text-muted-foreground">offen</span>
                          {row.reminder_count > 0 && (
                            <div className="text-amber-600 mt-1 inline-flex items-center gap-1" title={`Letzte Erinnerung: ${row.last_reminded_at?.slice(0, 16).replace("T", " ")}`}>
                              <Mail className="w-3 h-3" /> {row.reminder_count}× erinnert
                              {row.last_reminded_at && <span className="text-muted-foreground"> · {row.last_reminded_at.slice(0, 10)}</span>}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {row.paid ? (
                        <button onClick={() => undo(row)} disabled={busy} data-testid={`billing-undo-${key}`}
                          className="text-xs px-3 py-1.5 border border-border rounded-full hover:border-destructive hover:text-destructive inline-flex items-center gap-1">
                          <RotateCcw className="w-3 h-3" /> Rückgängig
                        </button>
                      ) : (
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => doPay(row, "twint")} disabled={busy} data-testid={`billing-pay-twint-${key}`}
                            className="text-xs px-3 py-1.5 rounded-full bg-primary/15 text-primary border border-primary/30 hover:bg-primary hover:text-primary-foreground transition-colors inline-flex items-center gap-1">
                            <Smartphone className="w-3 h-3" /> Twint
                          </button>
                          <button onClick={() => doPay(row, "cash")} disabled={busy} data-testid={`billing-pay-cash-${key}`}
                            className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-muted-foreground inline-flex items-center gap-1">
                            <Banknote className="w-3 h-3" /> Bar
                          </button>
                          <button onClick={() => doPay(row, "bank")} disabled={busy} title="Bank-Überweisung"
                            className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-muted-foreground inline-flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> Bank
                          </button>
                          <button onClick={() => remind(row)} disabled={busy} data-testid={`billing-remind-${key}`}
                            title="Freundliche Erinnerungs-Mail mit Twint-Nummer senden"
                            className="text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 inline-flex items-center gap-1">
                            <Mail className="w-3 h-3" /> Erinnerung
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent }) => {
  const cls = accent === "primary" ? "text-primary" : accent === "destructive" ? "text-destructive" : "";
  return (
    <div className="card-academy rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`text-2xl font-serif-display mt-1 ${cls}`}>{value}</div>
    </div>
  );
};

export default MonthlyBilling;
